<template>
  <VuelidateWrapper
    data-testid="file-input-vuelidate-wrapper"
    property="files"
    :errors="v$.$silentErrors"
    @dragover.prevent="highlight = true"
    @dragleave.prevent="highlight = false"
    @mouseover="highlight = true"
    @mouseleave="highlight = false"
    @drop.prevent="onDrop"
  >
    <div
      :tabindex="0"
      class="upload-or-drag-file"
      :class="{ highlight: highlight }"
    >
      <slot>
        <div class="flex items-start">
          <FontAwesomeIcon
            :icon="['far', 'arrow-up-from-bracket']"
            class="w-16 h-16 mr-8"
          />
          <b class="select-none">
            Drop {{ readableFileType }}
            {{ pluralize(props.fileLimit, 'file') }} here or browse
          </b>
        </div>
      </slot>
      <input
        ref="file"
        type="file"
        data-testid="file-input"
        class="file-input"
        :accept="acceptAttribute"
        @change="onSelectFiles"
      />
    </div>
  </VuelidateWrapper>
</template>

<script setup lang="ts">
import useVuelidate from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import { pluralize } from 'humanize-plus';
import { computed, ref } from 'vue';

import VuelidateWrapper from '@/components/VuelidateWrapper.vue';

const props = withDefaults(defineProps<Props>(), {
  fileLimit: 1,
});

const emit = defineEmits(['files-added', 'error']);

const CsvFileType = 'text/csv';
const XlsFileType = 'application/vnd.ms-excel';
const PdfFileType = 'application/pdf';

const FILE_CONFIG = {
  [CsvFileType]: { label: 'CSV', ext: '.csv' },
  [XlsFileType]: { label: 'CSV', ext: '.csv' },
  [PdfFileType]: { label: 'PDF', ext: '.pdf' },
};

type Props = {
  fileType: typeof CsvFileType | typeof PdfFileType | typeof XlsFileType;
  fileSizeLimit: number; // in Megabytes
  fileLimit?: number;
};

const files = ref(new Set<File>([]));

const acceptAttribute = computed(() => FILE_CONFIG[props.fileType]?.ext || '');
const readableFileType = computed(
  () => FILE_CONFIG[props.fileType]?.label || '',
);

const processedFileType = ref('');
const rules = computed(() => ({
  files: {
    invalidType: helpers.withMessage(
      () => {
        const baseMessage = `You may only upload ${props.fileLimit === 1 ? 'a' : ''} ${readableFileType.value} ${pluralize(props.fileLimit, 'file')}`;
        return processedFileType.value
          ? `${baseMessage}, you tried to upload a file with type [${processedFileType.value}]`
          : baseMessage;
      },
      (f: Set<File>) =>
        [...f].every((file) => {
          // For CSV files, check both MIME type and extension
          if (
            props.fileType === CsvFileType ||
            props.fileType === XlsFileType
          ) {
            return isCsvFile(file);
          }
          return file.type === props.fileType;
        }),
    ),
    fileSizeLimit: helpers.withMessage(
      `Your files exceed the ${props.fileSizeLimit}mb maximum`,
      (f: Set<File>) =>
        [...f].every((f) => f.size / 1000000 <= props.fileSizeLimit),
    ),
    fileLimit: helpers.withMessage(
      `You may only upload ${props.fileLimit} ${pluralize(props.fileLimit, 'file')}`,
      (f: Set<File>) => f.size <= props.fileLimit,
    ),
  },
}));

const v$ = useVuelidate(rules.value, { files });

function onDrop(ev: DragEvent) {
  highlight.value = false;
  files.value = new Set<File>();
  if (ev.dataTransfer?.files) processFiles(ev.dataTransfer.files);
}

function onSelectFiles(event: Event) {
  highlight.value = false;
  const target = event.target as HTMLInputElement;
  if (target.files?.length && target.files.length > 0) {
    processFiles(target.files);
  }
}

function processFiles(fileList: FileList) {
  files.value = new Set<File>();
  for (const file of fileList) {
    files.value.add(file);
    processedFileType.value = file.type;
  }

  if (v$.value.$invalid) {
    emit('error');
  } else {
    emit('files-added', [...files.value]);
  }
}

function isCsvFile(file: File): boolean {
  const fileName = file.name.toLowerCase();
  const hasValidExtension = fileName.endsWith('.csv');
  const hasValidMimeType =
    file.type === CsvFileType || file.type === XlsFileType;

  // For CSV validation, require both valid MIME type AND .csv extension
  // This blocks real Excel files (.xls, .xlsx) even if they have application/vnd.ms-excel MIME type
  return hasValidExtension && hasValidMimeType;
}

const highlight = ref(false);
</script>
<style lang="pcss" scoped>
@reference "../../css/theme.css";
.upload-or-drag-file {
  @apply border-2 border-neutral-border border-dashed w-full h-40 mt-12 rounded-8 flex items-center justify-center pb-40 pt-[44px] relative mb-6;

  &.highlight {
    @apply bg-secondary-background-weak border-secondary-border;
  }

  .file-input {
    @apply absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer;
  }
}
</style>
